import { checkoutTechnologyType } from "./merchant";
import { TTemplateView } from "./auto-coupons";
import { SelectorSource } from "./state-machine";

/**
 * How a merchant's effective map was resolved for testing/serving. The test
 * harness is source-agnostic: it consumes an `IEffectiveMerchantMap` regardless
 * of whether the selectors were bespoke-captured or derived from platform
 * defaults, so the same daily suite covers both populations.
 */
export enum MerchantMapSource {
  /** Agentic-captured canonical map (backend `models/merchant-map`). */
  BESPOKE = "bespoke",
  /** Pure platform defaults (`constants/defaults` `getDefaultSelectorsForType`). */
  PLATFORM_DEFAULT = "platform_default",
  /** Platform defaults merged with per-merchant overrides. */
  PLATFORM_OVERRIDE = "platform_override",
  /** Seeded from an existing human-built auto-coupon / SKU template (10k+). */
  TEMPLATE = "template",
  /** No bespoke map and no known platform — nothing to serve/test yet. */
  UNMAPPED = "unmapped",
}

/**
 * A single selector field on a merchant map. Carries an ordered fallback list
 * and health/freshness telemetry (mirrors `IntelligentSelector`) so the daily
 * test can record per-selector outcomes without a separate store.
 */
export interface IMerchantMapSelector {
  /** Primary selector. Null when this field is not captured. */
  selector: string | null;
  /** Ordered fallbacks tried when the primary fails to resolve. */
  fallbackSelectors: string[];
  /** Where this selector came from. */
  source: SelectorSource;
  /** Discovery/verification confidence, when known. */
  confidence?: number | null;
  /** Times this selector has resolved + worked during a test walk. */
  successCount: number;
  /** Times this selector has failed to resolve/work during a test walk. */
  failureCount: number;
  /** Last time the selector resolved and worked. */
  lastWorkedAt: Date | null;
  /** Last time the daily test checked this selector (pass or fail). */
  lastVerifiedAt: Date | null;
}

/** Coupon-application leg (mirrors the sold DTO's coupon selectors). */
export interface IMerchantMapCoupon {
  toggleCouponBoxSelector: IMerchantMapSelector | null;
  couponBoxSelector: IMerchantMapSelector | null;
  submitCodeButtonSelector: IMerchantMapSelector | null;
  finalPriceSelector: IMerchantMapSelector | null;
  failureAlertSelector: IMerchantMapSelector | null;
  successAlertSelector?: IMerchantMapSelector | null;
  removeCodeSelector?: IMerchantMapSelector | null;
}

/** One variant dimension (size, colour, ...) discovered on the product page. */
export interface IMerchantMapVariant {
  /** The dimension this control picks: "size", "colour", "capacity", … */
  variantType: string;
  selector: IMerchantMapSelector;
}

/** Product-page leg: land, choose a variant, add to cart. */
export interface IMerchantMapProductPage {
  urlPattern: string | null;
  addToCartSelector: IMerchantMapSelector | null;
  /**
   * Every variant control on the product page, one entry per dimension.
   *
   * This is the field to use. A product routinely needs more than one choice
   * made before it can be added to a basket — colour AND capacity, style AND
   * length — and naming one of them "size" picks an arbitrary winner and drops
   * the rest. Always an array; empty when the merchant has no variant controls.
   */
  variants: IMerchantMapVariant[];
}

/** Cart leg: verify the basket and proceed to checkout. */
export interface IMerchantMapCart {
  urlPattern: string | null;
  checkoutSelector: IMerchantMapSelector | null;
  basketAmountSelector?: IMerchantMapSelector | null;
  // Line-item (SKU) selectors — read the basket contents, mirroring the sold
  // SKU DTO. Populated from the existing SKU template where available.
  itemNameSelector?: IMerchantMapSelector | null;
  itemPriceSelector?: IMerchantMapSelector | null;
  itemQuantitySelector?: IMerchantMapSelector | null;
  itemCategorySelector?: IMerchantMapSelector | null;
  itemOriginalPriceSelector?: IMerchantMapSelector | null;
  itemProductIdSelector?: IMerchantMapSelector | null;
  itemProductUrlSelector?: IMerchantMapSelector | null;
  deliveryAmountSelector?: IMerchantMapSelector | null;
}

/** The three guest-checkout shipping fields the agent must fill. */
export interface IMerchantMapShippingFields {
  name: IMerchantMapSelector | null;
  address: IMerchantMapSelector | null;
  email: IMerchantMapSelector | null;
}

/** Checkout leg: fill shipping, continue to payment. */
export interface IMerchantMapCheckout {
  urlPattern?: string | null;
  shippingFields: IMerchantMapShippingFields;
  continueButtonSelector: IMerchantMapSelector | null;
  /**
   * False when the shipping leg can't be reached as a guest (merchant
   * `requiresAuth`). The leg is then reported "not applicable", not "failed".
   */
  shippingCapturable?: boolean;
}

/** The page-grouped selector content of a map, independent of storage. */
export interface IMerchantMapContent {
  domain: string;
  kindredMerchantId?: number | null;
  countryCode?: string | null;
  checkoutTechnology?: checkoutTechnologyType | null;
  /** desktop | mobile (maps are per-view, like coupon templates). */
  templateView: TTemplateView;
  checkoutUrlRegex: string | null;
  coupon: IMerchantMapCoupon;
  productPage: IMerchantMapProductPage;
  cart: IMerchantMapCart;
  checkout: IMerchantMapCheckout;
}

/** The persisted canonical (bespoke) merchant map. */
export interface IMerchantMap extends IMerchantMapContent {
  _id: string;
  merchantId?: string | null;
  /** Newest per-leg verification; freshness is a query off this. */
  lastVerifiedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * The source-agnostic map the test harness actually walks. Either a stored
 * bespoke map or one derived at resolve-time from platform defaults (+ any
 * per-merchant overrides). Never written back to the shared platform default.
 */
export interface IEffectiveMerchantMap extends IMerchantMapContent {
  source: MerchantMapSource;
  resolvedFrom: {
    canonicalMapId?: string | null;
    checkoutTechnology?: checkoutTechnologyType | null;
  };
}

// ── Manual editing ─────────────────────────────────────────

/** A field a person set by hand; automated captures and self-heal leave it alone while pinned. */
export interface IMerchantMapPin {
  path: string;
  by: string;
  byName: string | null;
  at: string;
  note: string | null;
}

export type MerchantMapFieldKind = "selector" | "pattern";

export interface IMerchantMapFieldDef {
  /** Path on the stored map, e.g. "cart.itemNameSelector". */
  path: string;
  label: string;
  section: "product" | "cart" | "checkout" | "shipping" | "coupon";
  kind: MerchantMapFieldKind;
  /** The Open Data merchant-map field it is served as. */
  apiField: string;
  /**
   * Where the API takes this field from: the stored merchant map, or the live SQL coupon/SKU templates.
   * Inferred by comparing served maps with stored ones; the partner API's own code isn't in this repo.
   */
  servedFrom: "map" | "sql";
}

/** Every stored field a person can edit, and what the Open Data API calls it. */
export const MERCHANT_MAP_EDITABLE_FIELDS: IMerchantMapFieldDef[] = [
  { path: "productPage.urlPattern", label: "Product URL pattern", section: "product", kind: "pattern", apiField: "product.productUrlPattern", servedFrom: "map" },
  { path: "productPage.addToCartSelector", label: "Add to bag button", section: "product", kind: "selector", apiField: "product.addToCartSelector", servedFrom: "map" },
  { path: "cart.urlPattern", label: "Cart URL pattern", section: "cart", kind: "pattern", apiField: "cart.cartUrlPattern", servedFrom: "map" },
  { path: "cart.checkoutSelector", label: "Cart checkout button", section: "cart", kind: "selector", apiField: "cart.cartCheckoutSelector", servedFrom: "map" },
  { path: "cart.itemNameSelector", label: "Item name", section: "cart", kind: "selector", apiField: "cart.itemNameSelector", servedFrom: "sql" },
  { path: "cart.itemPriceSelector", label: "Item price", section: "cart", kind: "selector", apiField: "cart.itemPriceSelector", servedFrom: "sql" },
  { path: "cart.itemOriginalPriceSelector", label: "Item original price", section: "cart", kind: "selector", apiField: "cart.itemOriginalPriceSelector", servedFrom: "sql" },
  { path: "cart.itemQuantitySelector", label: "Item quantity", section: "cart", kind: "selector", apiField: "cart.itemQuantitySelector", servedFrom: "sql" },
  { path: "cart.itemCategorySelector", label: "Item category", section: "cart", kind: "selector", apiField: "cart.itemCategorySelector", servedFrom: "sql" },
  { path: "cart.itemProductIdSelector", label: "Item product id", section: "cart", kind: "selector", apiField: "cart.itemProductIdSelector", servedFrom: "sql" },
  { path: "cart.itemProductUrlSelector", label: "Item product link", section: "cart", kind: "selector", apiField: "cart.itemProductUrlSelector", servedFrom: "sql" },
  { path: "cart.basketAmountSelector", label: "Basket subtotal", section: "cart", kind: "selector", apiField: "cart.basketAmountSelector", servedFrom: "sql" },
  { path: "cart.deliveryAmountSelector", label: "Delivery amount", section: "cart", kind: "selector", apiField: "checkout.deliveryAmountSelector", servedFrom: "sql" },
  { path: "checkoutUrlRegex", label: "Checkout page URL regex", section: "checkout", kind: "pattern", apiField: "checkout.checkoutPageUrlRegex", servedFrom: "sql" },
  { path: "checkout.urlPattern", label: "Checkout URL pattern", section: "checkout", kind: "pattern", apiField: "checkout.checkoutUrlPattern", servedFrom: "map" },
  { path: "checkout.continueButtonSelector", label: "Continue button", section: "checkout", kind: "selector", apiField: "checkout.continueButtonSelector", servedFrom: "map" },
  { path: "coupon.toggleCouponBoxSelector", label: "Show coupon field", section: "coupon", kind: "selector", apiField: "checkout.toggleCouponBoxSelector", servedFrom: "sql" },
  { path: "coupon.couponBoxSelector", label: "Coupon field", section: "coupon", kind: "selector", apiField: "checkout.couponBoxSelector", servedFrom: "sql" },
  { path: "coupon.submitCodeButtonSelector", label: "Apply button", section: "coupon", kind: "selector", apiField: "checkout.submitCodeButtonSelector", servedFrom: "sql" },
  { path: "coupon.finalPriceSelector", label: "Order total", section: "coupon", kind: "selector", apiField: "checkout.finalPriceSelector", servedFrom: "sql" },
  { path: "coupon.failureAlertSelector", label: "Code rejected message", section: "coupon", kind: "selector", apiField: "checkout.failureAlertSelector", servedFrom: "sql" },
  { path: "coupon.successAlertSelector", label: "Code accepted message", section: "coupon", kind: "selector", apiField: "checkout.successAlertSelector", servedFrom: "sql" },
  { path: "coupon.removeCodeSelector", label: "Remove code", section: "coupon", kind: "selector", apiField: "checkout.removeCodeSelector", servedFrom: "sql" },
  { path: "checkout.shippingFields.name", label: "Shipping name", section: "shipping", kind: "selector", apiField: "shipping.shippingNameSelector", servedFrom: "map" },
  { path: "checkout.shippingFields.address", label: "Shipping address", section: "shipping", kind: "selector", apiField: "shipping.shippingAddressSelector", servedFrom: "map" },
  { path: "checkout.shippingFields.email", label: "Shipping email", section: "shipping", kind: "selector", apiField: "shipping.shippingEmailSelector", servedFrom: "map" },
];

/**
 * Whether the latest journey test proved the stored value, using the same rule as the data-asa
 * TemplateDataCopy merge: only proven map values are published to the Open Data API.
 */
export interface IMerchantMapFieldProof {
  status: "proven" | "failed" | "not-resolved" | "other-value" | "untested";
  /** The value the latest journey test passed, when it isn't the stored one. */
  provenValue: string | null;
  testedAt: string | null;
  reason: string | null;
}

export interface IMerchantMapFieldView extends IMerchantMapFieldDef {
  value: string | null;
  source: SelectorSource | null;
  successCount: number | null;
  failureCount: number | null;
  lastWorkedAt: string | null;
  pin: IMerchantMapPin | null;
  /** What the Open Data API serves for this field right now; null when empty or not served. */
  servedValue: string | null;
  proof: IMerchantMapFieldProof;
  /** The AutoCouponTemplate staging field a save also writes; null when the field has no staging slot. */
  stagingField: string | null;
  /** The value in the staging template for this view; null when unset or there is no staging template. */
  stagedValue: string | null;
}

/** The AutoCouponTemplate staging doc for this domain and view, which a person puts live to SQL. */
export interface IMerchantMapStaging {
  templateId: string;
  invalid: boolean;
  syncedToSqlAt: string | null;
  updatedAt: string | null;
  /** Never put live, changed since the last put-live, or flagged by the SQL diff scan. */
  unsynced: boolean;
  /** Set when SKU values come from the other view's doc, as the put-live flow falls back to. */
  skuFromView: TTemplateView | null;
}

export interface IMerchantMapEditorView {
  domain: string;
  templateView: TTemplateView;
  exists: boolean;
  checkoutTechnology: checkoutTechnologyType | null;
  lastVerifiedAt: string | null;
  updatedAt: string | null;
  /** The live Open Data map for this domain and view, as served now; null when the lookup failed. */
  served: { found: boolean; viewType: string } | null;
  /** Null when there is no staging template for this domain and view. */
  staging: IMerchantMapStaging | null;
  fields: IMerchantMapFieldView[];
  /** Only on a save response: SQL fields written to staging, or why staging was skipped. */
  stageResult?: { staged: string[]; skipped: string | null };
}

export interface IMerchantMapFieldEdit {
  path: string;
  /** New value; null clears the field. Omit to only change the pin. */
  value?: string | null;
  /** Keep this value through automated recaptures and self-heal. */
  pin: boolean;
}

export interface IMerchantMapEditRequest {
  edits: IMerchantMapFieldEdit[];
  /** Why the change was made; required, shown in the change history. */
  comment: string;
}
